import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../content/hooks';
import type { ListProductModel, Order, ProductSortingFields, ProductsFiltersProps } from '../models';

interface IProductsContext {
  productsUpdate: boolean;
  toggleProductsUpdate: () => void;
  products: ListProductModel[];
  setProducts: (val: ListProductModel[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  count: number;
  setCount: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: ProductSortingFields;
  setOrderBy: (val: ProductSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: ProductSortingFields) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  filters: ProductsFiltersProps;
  setFilters: (val: ProductsFiltersProps) => void;
  resetFilters: () => void;
}

export const ProductsContext = createContext<IProductsContext>({} as IProductsContext);

export const ProductsProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [productsUpdate, setProductsUpdate] = useState(false);
  const toggleUpdate = (): void => setProductsUpdate(!productsUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<ProductSortingFields>(toggleUpdate, 'name', 'asc');

  const [products, setProducts] = useState<ListProductModel[]>([]);
  const [loading, setLoading] = useState(false);
  const emptyFilters: ProductsFiltersProps = {
    brewerName: undefined,
    style: undefined,
    containerType: undefined,
    nozzleType: undefined,
    tapSealType: undefined,
    couplerType: undefined,
    alcoholByVolumeBand: undefined,
    vegan: undefined,
    glutenFree: undefined,
    status: undefined
  };
  const [filters, setFilters] = useState<ProductsFiltersProps>(emptyFilters);
  const resetFilters = (): void => {
    setFilters(emptyFilters);
    setPage(0);
    setLimit(20);
  };

  return (
    <ProductsContext.Provider
      value={{
        filters,
        setFilters,
        resetFilters,
        productsUpdate,
        toggleProductsUpdate: toggleUpdate,
        products,
        setProducts,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};
